import { sessaoSchema } from '@locaveri/shared'

import type { AuthState } from '@/stores/auth-store'
import { useAuthStore } from '@/stores/auth-store'

import { ROTAS_DE_AUTENTICACAO } from './endpoints'
import { buildHeaders, buildUrl, readBody } from './http'

/**
 * Ciclo de vida da sessão: restaurar no boot, renovar quando expira, encerrar.
 *
 * ---------------------------------------------------------------------------
 * POR QUE A RENOVAÇÃO É DEDUPLICADA
 * ---------------------------------------------------------------------------
 * Uma tela do painel dispara várias requisições ao mesmo tempo (lista +
 * contadores + filtros). Quando o access token expira, TODAS levam 401 no mesmo
 * instante. Sem deduplicação, cada uma pediria a sua renovação — e com refresh
 * ROTATIVO com detecção de reuso (Fase 1 da API) a segunda chamada apresenta um
 * token já rotacionado, o servidor conclui que a família foi copiada e derruba
 * a sessão inteira. Ou seja: o bug não seria "uma requisição a mais", seria
 * "o usuário é deslogado exatamente quando o painel está mais ocupado".
 *
 * Por isso existe uma única promessa em voo: a primeira chamada renova, as
 * demais aguardam o mesmo resultado.
 *
 * ---------------------------------------------------------------------------
 * E ENTRE ABAS, UM TRAVA NO NAVEGADOR
 * ---------------------------------------------------------------------------
 * A promessa única vale para UMA aba. Com o refresh no cookie — que o
 * navegador divide entre todas as abas —, duas abas renovando ao mesmo tempo
 * apresentariam o MESMO token, e a segunda seria lida como reuso: família
 * derrubada, as duas abas deslogadas. Antes do cookie existir de verdade isso
 * não acontecia só porque a segunda aba nem tinha sessão.
 *
 * `navigator.locks` (Web Locks API) serializa a renovação entre as abas da
 * mesma origem: a segunda espera a primeira terminar e renova com o cookie
 * que a primeira já trocou pelo sucessor. Onde a API não existe (navegador
 * muito antigo, ambiente de teste), a renovação corre como antes.
 */

/** O nome da trava entre abas. Uma só para o painel inteiro. */
export const TRAVA_DE_RENOVACAO = 'locaveri:renovacao-de-sessao'

function comTravaEntreAbas<T>(tarefa: () => Promise<T>): Promise<T> {
  const travas = typeof navigator === 'undefined' ? undefined : navigator.locks
  // `LockManager.request` devolve a promessa da própria tarefa.
  return travas === undefined ? tarefa() : travas.request(TRAVA_DE_RENOVACAO, tarefa)
}

/** Margem antes do vencimento. Evita gastar um 401 para descobrir o óbvio. */
const MARGEM_DE_RENOVACAO_MS = 30_000

/** A promessa em voo. `null` quando não há renovação acontecendo. */
let renovacaoEmAndamento: Promise<string | null> | null = null

/** A restauração de boot também acontece uma vez só. */
let restauracaoEmAndamento: Promise<void> | null = null

interface ResultadoDaRenovacao {
  readonly accessToken: string | null
  /** `true` quando o servidor recusou o refresh — a sessão acabou de fato. */
  readonly sessaoInvalida: boolean
}

async function pedirNovoAccessToken(): Promise<ResultadoDaRenovacao> {
  const { refreshToken } = useAuthStore.getState()

  // Com o cookie httpOnly funcionando, o corpo vai vazio e o servidor lê o
  // cookie. O campo só é enviado no plano B (ver comentário do store).
  const corpo = refreshToken === null ? {} : { refreshToken }

  let resposta: Response
  try {
    resposta = await fetch(buildUrl(ROTAS_DE_AUTENTICACAO.refresh), {
      method: 'POST',
      headers: buildHeaders({ hasBody: true, accessToken: null }),
      // Sem isto o cookie httpOnly não viaja e a renovação nunca funcionaria.
      credentials: 'include',
      body: JSON.stringify(corpo),
    })
  } catch {
    // Rede fora do ar não é sessão inválida: derrubar o usuário aqui seria
    // trocar uma falha temporária por um logout definitivo.
    return { accessToken: null, sessaoInvalida: false }
  }

  if (!resposta.ok) {
    // 401/403 = o refresh não vale mais (expirou, foi rotacionado ou a família
    // caiu por reuso). 5xx = problema do servidor, e a sessão pode continuar.
    return { accessToken: null, sessaoInvalida: resposta.status >= 400 && resposta.status < 500 }
  }

  const payload = await readBody(resposta)
  const sessao = sessaoSchema.safeParse(payload)

  if (!sessao.success) {
    // Resposta fora do contrato: não dá para confiar no que veio.
    return { accessToken: null, sessaoInvalida: true }
  }

  useAuthStore.getState().definirSessao(sessao.data)
  return { accessToken: sessao.data.accessToken, sessaoInvalida: false }
}

/**
 * `true` quando a sessão em memória mudou desde o instante `antes` — ou seja,
 * quando alguém entrou (ou saiu) enquanto a renovação estava no ar.
 */
function sessaoTrocouDesde(antes: Pick<AuthState, 'accessToken' | 'usuario'>): boolean {
  const agora = useAuthStore.getState()

  return agora.accessToken !== antes.accessToken || agora.usuario?.id !== antes.usuario?.id
}

async function executarRenovacao(): Promise<string | null> {
  // Fotografia de QUAL sessão esta renovação está tentando salvar.
  const { accessToken, usuario } = useAuthStore.getState()

  const resultado = await comTravaEntreAbas(pedirNovoAccessToken)

  // A recusa só derruba a sessão que originou a renovação. Sem esta guarda, o
  // 401 atrasado da restauração de boot (sem cookie) chegaria DEPOIS de a
  // pessoa já ter entrado pelo formulário e a expulsaria logo após o login —
  // uma corrida estreita, mas real em rede lenta.
  if (resultado.sessaoInvalida && !sessaoTrocouDesde({ accessToken, usuario })) {
    useAuthStore.getState().limparSessao()
  }

  return resultado.accessToken
}

/**
 * Renova o access token. Chamadas concorrentes compartilham a MESMA promessa —
 * uma requisição de rede, N esperando.
 */
export function renovarAccessToken(): Promise<string | null> {
  renovacaoEmAndamento ??= executarRenovacao().finally(() => {
    renovacaoEmAndamento = null
  })

  return renovacaoEmAndamento
}

/** `true` se o token não existe ou vence dentro da margem. */
export function precisaRenovar(): boolean {
  const { status, accessToken, expiraEm } = useAuthStore.getState()

  if (status !== 'autenticada') {
    return false
  }

  return (
    accessToken === null || expiraEm === null || expiraEm - Date.now() <= MARGEM_DE_RENOVACAO_MS
  )
}

/**
 * Token pronto para o cabeçalho `Authorization`. Renova ANTES de mandar quando
 * já está vencendo — 401 previsível não precisa ser esperado para ser tratado.
 */
export async function obterAccessToken(): Promise<string | null> {
  if (precisaRenovar()) {
    return renovarAccessToken()
  }

  return useAuthStore.getState().accessToken
}

/**
 * Reidrata a sessão no boot a partir do cookie httpOnly. Roda UMA vez por
 * carregamento da página; o guard de rota aguarda por ela antes de decidir.
 */
export function garantirSessaoRestaurada(): Promise<void> {
  restauracaoEmAndamento ??= (async () => {
    const store = useAuthStore.getState()

    if (store.status !== 'desconhecida') {
      return
    }

    store.iniciarRestauracao()
    await renovarAccessToken()

    // Se a renovação não produziu sessão (sem cookie, cookie expirado ou API
    // fora do ar), o estado precisa sair de `restaurando` — senão o guard
    // esperaria para sempre e a tela ficaria em branco.
    if (useAuthStore.getState().status === 'restaurando') {
      useAuthStore.getState().definirAnonima()
    }
  })()

  return restauracaoEmAndamento
}

/**
 * Encerra a sessão: avisa o servidor (para revogar a família do refresh) e
 * limpa tudo o que existe no cliente.
 *
 * O `await` do servidor é BEST EFFORT de propósito: se a API estiver fora do
 * ar, o usuário ainda assim precisa sair — o pior desfecho possível aqui é
 * "cliquei em sair e continuei logado".
 */
export async function encerrarSessao(
  options: { readonly todasAsSessoes?: boolean } = {},
): Promise<void> {
  const { accessToken, refreshToken } = useAuthStore.getState()
  const todasAsSessoes = options.todasAsSessoes ?? false

  try {
    await fetch(buildUrl(ROTAS_DE_AUTENTICACAO.logout), {
      method: 'POST',
      headers: buildHeaders({ hasBody: true, accessToken }),
      credentials: 'include',
      body: JSON.stringify(
        refreshToken === null ? { todasAsSessoes } : { refreshToken, todasAsSessoes },
      ),
    })
  } catch {
    // Sem rede não há o que avisar. O cliente limpa mesmo assim.
  } finally {
    useAuthStore.getState().limparSessao()
  }
}

/**
 * Zera os singletons de renovação e restauração.
 *
 * Existe para os testes: sem isto, o `renovacaoEmAndamento` de um caso vazaria
 * para o seguinte e a asserção de deduplicação passaria por engano.
 */
export function redefinirSessaoParaTestes(): void {
  renovacaoEmAndamento = null
  restauracaoEmAndamento = null
  useAuthStore.setState({
    status: 'desconhecida',
    usuario: null,
    accessToken: null,
    refreshToken: null,
    expiraEm: null,
    desafio: null,
  })
}
